import { BoxV2 as Box, Wizard, WizardStep } from 'portal-commons';
import { FunctionComponent, useState } from 'react';

import BrandDetailsForm from './BrandDetailsForm';
import FormModal from './FormModal';
import SenderIdConfigurationForm from './SenderIdConfigurationForm';
import TrademarkVerificationForm from './TrademarkVerificationForm';
import {
  DefaultBrandDetailsFormValues,
  DefaultSenderIdConfigurationFormValues,
  DefaultTrademarkVerificationFormValues,
} from './constants';
import {
  brandController,
  senderIdController,
  trademarkController,
} from '@/apis/core';

interface BrandRegistrationProps {
  onCancel?: () => void;
  onSucceed?: (brandUid: string) => void;
}

enum StepKey {
  Identity = 'identity',
  Trademarks = 'trademarks',
  SenderIds = 'senderIds',
}

const Steps: WizardStep[] = [
  {
    key: StepKey.Identity,
    label: 'Identity',
  },
  {
    key: StepKey.Trademarks,
    label: 'Trademarks',
  },
  {
    key: StepKey.SenderIds,
    label: 'Sender IDs',
  },
];

const BrandRegistration: FunctionComponent<BrandRegistrationProps> = ({
  onCancel,
  onSucceed,
}) => {
  const [step, setStep] = useState(0);
  const [brandDetailsFormValues, setBrandDetailsFormValues] = useState(
    DefaultBrandDetailsFormValues
  );
  const [trademarkVerificationFormValues, setTrademarkVerificationFormValues] =
    useState(DefaultTrademarkVerificationFormValues);
  const [senderIdConfigurationFormValues, setSenderIdConfigurationFormValues] =
    useState(DefaultSenderIdConfigurationFormValues);
  const [proceeding, setProceeding] = useState(false);
  const activeStepKey = Steps[step].key;

  const handleCancel = () => {
    setBrandDetailsFormValues(DefaultBrandDetailsFormValues);
    setTrademarkVerificationFormValues(DefaultTrademarkVerificationFormValues);
    setSenderIdConfigurationFormValues(DefaultSenderIdConfigurationFormValues);
    onCancel?.();
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    setProceeding(true);
    const brand = await brandController.createBrand({
      ...brandDetailsFormValues,
      nonBlocking: true,
    });
    if (brand) {
      const pendingRequests: Promise<unknown>[] = [];
      const { registrationId, trademarkText, patentOffice } =
        trademarkVerificationFormValues;
      if (registrationId && trademarkText && patentOffice) {
        pendingRequests.push(
          trademarkController
            .createTrademarks(brand.uid, trademarkVerificationFormValues)
            .then((trademarks) => {
              if (trademarks.length > 0) {
                trademarks.forEach((trademark) => {
                  trademarkController.verifyTrademark(brand.uid, trademark.uid);
                });
              }
            })
        );
      }
      const data = senderIdConfigurationFormValues.senderIds.filter((item) => {
        const { senderId, associatedDcaList } = item;
        return senderId && associatedDcaList;
      });
      if (data.length > 0) {
        pendingRequests.push(
          senderIdController.createSenderIds(brand.uid, data)
        );
      }
      if (pendingRequests.length > 0) {
        await Promise.allSettled(pendingRequests);
      }
      onSucceed?.(brand.uid);
    }
    setProceeding(false);
  };

  return (
    <FormModal onClose={handleCancel} testId="brandRegistrationModal">
      <Box sx={{ pb: '20px' }}>
        <Wizard steps={Steps} activeKey={activeStepKey} />
      </Box>
      {activeStepKey === StepKey.Identity && (
        <BrandDetailsForm
          defaultValues={brandDetailsFormValues}
          onCancel={handleCancel}
          onNext={(data) => {
            setBrandDetailsFormValues(data);
            handleNext();
          }}
        />
      )}
      {activeStepKey === StepKey.Trademarks && (
        <TrademarkVerificationForm
          defaultValues={trademarkVerificationFormValues}
          onChange={setTrademarkVerificationFormValues}
          onBack={handleBack}
          onNext={handleNext}
        />
      )}
      {activeStepKey === StepKey.SenderIds && (
        <SenderIdConfigurationForm
          proceeding={proceeding}
          defaultValues={senderIdConfigurationFormValues}
          onChange={setSenderIdConfigurationFormValues}
          onBack={handleBack}
          onSubmit={() => {
            handleSubmit();
          }}
        />
      )}
    </FormModal>
  );
};

export default BrandRegistration;
